// Localização: frontend/src/pages/Financeiro/ContasPagarPage.jsx
// Página principal para gerenciar Contas a Pagar

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from '../../components/UI/Modal';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionContext';
import { useContasPagar } from '../../hooks/useContasPagar';
import ListaContasPagar from '../../components/Financeiro/ListaContasPagar';
import FormContaPagar from '../../components/Financeiro/FormContaPagar';
import ModalPagamento from '../../components/Financeiro/ModalPagamento';
import AcessoRapidoFinanceiro from '../../components/Financeiro/AcessoRapidoFinanceiro';
import FloatingCalendarFilter from '../../components/Financeiro/FloatingCalendarFilter';
import CollapsibleStatusFilter from '../../components/Financeiro/CollapsibleStatusFilter';
import PrimaryButton from '../../components/Common/PrimaryButton';
import { Plus, Calendar, Tag } from 'lucide-react';

const ContasPagarPage = () => {
    const { isAuthenticated } = useAuth();
    const { checkPermission } = usePermissions();

    const {
        contasPagar,
        isLoading,
        pagination,
        fetchContasPagar,
        createContaPagar,
        updateContaPagar,
        deleteContaPagar,
        registrarPagamentoTotal,
        registrarPagamentoParcial
    } = useContasPagar();

    // Estados de UI
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalPagamentoOpen, setIsModalPagamentoOpen] = useState(false);
    const [editingConta, setEditingConta] = useState(null);
    const [contaParaPagar, setContaParaPagar] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // Inicializar com o mês atual
    const [periodoFluxo, setPeriodoFluxo] = useState('mes');
    const [showPeriodFilter, setShowPeriodFilter] = useState(false);
    const periodFilterRef = useRef(null);
    const periodButtonRef = useRef(null);
    const [showStatusFilter, setShowStatusFilter] = useState(false);
    const statusFilterRef = useRef(null);
    const statusButtonRef = useRef(null);
    const [filters, setFilters] = useState(() => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
            startDate: firstDay.toISOString().split('T')[0],
            endDate: lastDay.toISOString().split('T')[0],
            status: ['Pendente', 'Vencido'],
            search: ''
        };
    });

    // Permissões - se não encontrar, assume true (permissão concedida por padrão)
    const canCreate = checkPermission('financeiro', 'can_create') !== false;
    const canEdit = checkPermission('financeiro', 'can_edit') !== false;
    const canDelete = checkPermission('financeiro', 'can_delete') !== false;
    const canAccess = checkPermission('financeiro', 'can_access') !== false;

    // Ref para evitar chamadas duplicadas durante o carregamento
    const isLoadingRef = useRef(false);

    // Função centralizada para carregar contas com os filtros atuais
    const carregarContas = useCallback(() => {
        // Evitar chamadas duplicadas durante o carregamento
        if (isLoadingRef.current) {
            return;
        }

        if (isAuthenticated && canAccess) {
            isLoadingRef.current = true;
            
            const params = {
                page: currentPage,
                limit: 20
            };

            // Só adicionar filtros se tiverem valor
            if (filters.startDate) {
                params.data_inicio = filters.startDate;
            }
            if (filters.endDate) {
                params.data_fim = filters.endDate;
            }
            if (filters.status && filters.status.length > 0) {
                params.status = filters.status.join(',');
            }
            if (filters.search && filters.search.trim()) {
                params.search = filters.search.trim();
            }

            fetchContasPagar(params).finally(() => {
                isLoadingRef.current = false;
            });
        }
    }, [currentPage, filters, isAuthenticated, canAccess, fetchContasPagar]);

    // Carregar dados e aplicar paginação
    useEffect(() => {
        // Evitar chamadas quando os modais estão abertos
        if (isModalOpen || isModalPagamentoOpen) {
            return;
        }
        carregarContas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, filters.startDate, filters.endDate, filters.status, filters.search, isAuthenticated, canAccess]);

    const handleDateRangeChange = useCallback((start, end) => {
        setFilters(prev => {
            // Evitar atualização desnecessária se os valores são os mesmos
            if (prev.startDate === start && prev.endDate === end) {
                return prev;
            }
            return {
                ...prev,
                startDate: start,
                endDate: end
            };
        });
        setCurrentPage(1);
    }, []);

    const handlePeriodoChange = (periodo) => {
        setPeriodoFluxo(periodo);
        
        // Se for 'custom', não recalcular datas (elas já foram definidas pelo onDateRangeChange)
        if (periodo === 'custom') {
            setCurrentPage(1);
            return;
        }

        const today = new Date();
        const start = new Date();
        const end = new Date();

        if (periodo === 'dia') {
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
        } else if (periodo === 'semana') {
            const dayOfWeek = today.getDay();
            start.setDate(today.getDate() - dayOfWeek);
            end.setDate(start.getDate() + 6);
        } else if (periodo === 'mes') {
            start.setDate(1);
            end.setMonth(end.getMonth() + 1);
            end.setDate(0);
        }

        setFilters(prev => ({
            ...prev,
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        }));
        setCurrentPage(1);
    };

    const handleOpenModal = (conta = null) => {
        setEditingConta(conta);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingConta(null);
    };

    const handleSave = async (formData) => {
        setIsSaving(true);
        try {
            if (editingConta) {
                await updateContaPagar(editingConta.id, formData);
                alert('Conta a pagar atualizada com sucesso!');
            } else {
                await createContaPagar(formData);
                alert('Conta a pagar criada com sucesso!');
            }
            handleCloseModal();
            carregarContas();
        } catch (error) {
            alert(error.message || 'Erro ao salvar conta a pagar.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteContaPagar(id);
            alert('Conta a pagar cancelada com sucesso!');
            carregarContas();
        } catch (error) {
            alert(error.message || 'Erro ao cancelar conta a pagar.');
        }
    };

    const handlePagar = (conta) => {
        setContaParaPagar(conta);
        setIsModalPagamentoOpen(true);
    };

    const handleConfirmarPagamento = async (data) => {
        setIsSaving(true);
        try {
            if (data.valor) {
                // Pagamento parcial
                await registrarPagamentoParcial(contaParaPagar.id, data);
                alert('Pagamento parcial registrado com sucesso!');
            } else {
                // Pagamento total
                await registrarPagamentoTotal(contaParaPagar.id, data);
                alert('Pagamento registrado com sucesso!');
            }
            setIsModalPagamentoOpen(false);
            setContaParaPagar(null);
            carregarContas();
        } catch (error) {
            alert(error.message || 'Erro ao registrar pagamento.');
        } finally {
            setIsSaving(false);
        }
    };

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleClickOutside = (event) => {
            if (
                periodFilterRef.current &&
                periodButtonRef.current &&
                !periodFilterRef.current.contains(event.target) &&
                !periodButtonRef.current.contains(event.target)
            ) {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showPeriodFilter]);

    // Fechar dropdown ao pressionar Escape
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showPeriodFilter]);

    // Fechar dropdown de Status ao clicar fora
    useEffect(() => {
        if (!showStatusFilter) return;

        const handleClickOutside = (event) => {
            if (
                statusFilterRef.current &&
                statusButtonRef.current &&
                !statusFilterRef.current.contains(event.target) &&
                !statusButtonRef.current.contains(event.target)
            ) {
                setShowStatusFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showStatusFilter]);

    // Fechar dropdown de Status ao pressionar Escape
    useEffect(() => {
        if (!showStatusFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowStatusFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showStatusFilter]);

    if (!canAccess) {
        return (
            <div className="p-6">
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-yellow-800">
                        Você não tem permissão para acessar esta funcionalidade.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen w-full">
            {/* Acesso Rápido às Funções - Posição Fixa - Centralizado - Abaixo do Header */}
            <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-20 lg:top-20">
                <AcessoRapidoFinanceiro />
            </div>

            {/* Botões Flutuantes à Direita - Período e Status */}
            <div className="fixed top-20 right-2 sm:right-4 lg:right-8 z-30 flex flex-col gap-2">
                {/* Botão de Período */}
                <div ref={periodButtonRef}>
                    <button
                        onClick={() => setShowPeriodFilter(!showPeriodFilter)}
                        className={`
                            flex items-center justify-center gap-2
                            px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg
                            transition-all duration-200 font-medium 
                            text-xs sm:text-sm
                            border shadow-lg hover:shadow-xl
                            backdrop-blur-sm
                            ${showPeriodFilter 
                                ? 'bg-gray-100 text-gray-700 border-gray-300 border-[0.5px] shadow-xl font-semibold' 
                                : 'bg-white/80 text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }
                        `}
                        title={showPeriodFilter ? 'Ocultar filtro de período' : 'Mostrar filtro de período'}
                    >
                        <Calendar className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showPeriodFilter ? 'scale-110' : ''} transition-transform`} />
                        <span className="hidden sm:inline whitespace-nowrap">Período</span>
                    </button>
                </div>

                {/* Botão de Status */}
                <div ref={statusButtonRef}>
                    <button
                        onClick={() => setShowStatusFilter(!showStatusFilter)}
                        className={`
                            flex items-center justify-center gap-2
                            px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg
                            transition-all duration-200 font-medium 
                            text-xs sm:text-sm
                            border shadow-lg hover:shadow-xl
                            backdrop-blur-sm
                            ${showStatusFilter
                                ? 'bg-gray-100 text-gray-700 border-gray-300 border-[0.5px] shadow-xl font-semibold' 
                                : 'bg-white/80 text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }
                        `}
                        title={showStatusFilter ? 'Ocultar filtro de status' : 'Mostrar filtro de status'}
                    >
                        <Tag className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showStatusFilter ? 'scale-110' : ''} transition-transform`} />
                        <span className="hidden sm:inline whitespace-nowrap">Status</span>
                    </button>
                </div>
            </div>

            {/* Filtro de Calendário Flutuante - Dropdown */}
            {showPeriodFilter && !isModalOpen && !isModalPagamentoOpen && (
                <div ref={periodFilterRef} className="fixed top-20 left-2 right-2 sm:left-auto sm:right-4 sm:top-36 lg:right-8 z-[100] w-auto sm:w-auto sm:max-w-md max-h-[calc(100vh-6rem)] overflow-y-auto">
                    <FloatingCalendarFilter
                        periodo={periodoFluxo}
                        onPeriodoChange={handlePeriodoChange}
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                        onDateRangeChange={handleDateRangeChange}
                        noNegativeMargin={true}
                        customZIndex="z-[100]"
                        showOnlyContent={true}
                        onClose={() => setShowPeriodFilter(false)}
                    />
                </div>
            )}

            {/* Filtro de Status Flutuante - Dropdown */}
            {showStatusFilter && !isModalOpen && !isModalPagamentoOpen && (
                <div ref={statusFilterRef} className="fixed top-32 left-2 right-2 sm:left-auto sm:right-4 sm:top-48 lg:right-8 z-[100] w-auto sm:w-auto sm:min-w-[200px] max-h-[calc(100vh-8rem)] overflow-y-auto">
                    <CollapsibleStatusFilter
                        status={filters.status}
                        onStatusChange={(newStatus) => {
                            setFilters(prev => ({ ...prev, status: newStatus }));
                            setCurrentPage(1);
                        }}
                        type="pagar"
                        showOnlyContent={true}
                    />
                </div>
            )}

            {/* Conteúdo com espaçamento para o AcessoRapido fixo */}
            <div className="pt-32 pb-8 px-4 sm:px-6 lg:px-10 space-y-6">

            <div className="flex items-center justify-end">
                {canCreate && (
                    <PrimaryButton
                        onClick={() => handleOpenModal()}
                        className="flex items-center gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        Nova Conta a Pagar
                    </PrimaryButton>
                )}
            </div>

            {/* Lista de contas */}
            <ListaContasPagar
                contasPagar={contasPagar}
                isLoading={isLoading}
                onEdit={handleOpenModal}
                onDelete={handleDelete}
                onPagar={handlePagar}
                canEdit={canEdit}
                canDelete={canDelete}
            />

            {/* Paginação */}
            {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between bg-white px-4 py-3 rounded-lg border border-gray-200">
                    <div className="text-sm text-gray-700">
                        Mostrando {((pagination.page - 1) * pagination.limit) + 1} a{' '}
                        {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                        {pagination.total} resultados
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            Anterior
                        </button>
                        <span className="px-3 py-1 text-sm text-gray-700">
                            Página {currentPage} de {pagination.totalPages}
                        </span>
                        <button
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            disabled={currentPage === pagination.totalPages}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            Próxima
                        </button>
                    </div>
                </div>
            )}

            {/* Modal de formulário */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingConta ? 'Editar Conta a Pagar' : 'Nova Conta a Pagar'}
            >
                <FormContaPagar
                    conta={editingConta}
                    onSave={handleSave}
                    onCancel={handleCloseModal}
                    isLoading={isSaving}
                />
            </Modal>

            {/* Modal de pagamento */}
            <ModalPagamento
                isOpen={isModalPagamentoOpen}
                onClose={() => {
                    setIsModalPagamentoOpen(false);
                    setContaParaPagar(null);
                }}
                conta={contaParaPagar}
                onConfirm={handleConfirmarPagamento}
                isLoading={isSaving}
            />
            </div>
        </div>
    );
};

export default ContasPagarPage;
